import { comuneLabel } from '@sse/gazetteer';
import { CATEGORIES, type Event } from '@sse/schema';
import {
  CopyCheck,
  ExternalLink,
  ImageIcon,
  Lock,
  Merge,
  Plus,
  Search,
  Star,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ComunePicker } from '../components/comune-picker';
import { MonthPicker, monthLabel, monthRange } from '../components/month-picker';
import {
  StatusBadge,
  btnGhost,
  btnPrimary,
  btnSecondary,
  inputBaseCls,
  inputCls,
} from '../components/ui';
import { ApiError, type AdminEventRow } from '../lib/api';
import { STATUS_TABS, categoryLabel, formatRange, formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

const STATUSES: Event['status'][] = ['published', 'draft', 'cancelled', 'archived'];

type BulkAction = Event['status'] | 'category';
const BULK_LABEL: Record<Event['status'], { button: string; confirm: string }> = {
  published: { button: 'Pubblica', confirm: 'pubblicare' },
  draft: { button: 'Metti in bozza', confirm: 'togliere dal sito (bozza)' },
  cancelled: { button: 'Segna annullati', confirm: 'segnare come annullati' },
  archived: { button: 'Archivia', confirm: 'archiviare' },
};
const WHEN = [
  { id: 'upcoming', label: 'In corso e futuri' },
  { id: 'past', label: 'Conclusi' },
  { id: 'all', label: 'Tutti' },
] as const;

interface Page {
  items: AdminEventRow[];
  cursor?: string;
  scanned: number;
}

/**
 * Elenco degli eventi: filtri nell'URL (condivisibili e conservati tornando dalla scheda),
 * ricerca testuale e caricamento progressivo.
 */
export function EventsList() {
  const { api, config, canEdit } = useSession();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const status = (sp.get('status') as Event['status'] | null) ?? 'published';
  const when = sp.get('when') ?? 'upcoming';
  const comune = sp.get('comune') ?? undefined;
  const category = sp.get('category') ?? '';
  const q = sp.get('q') ?? '';
  // mese "YYYY-MM": alternativa al periodo (in corso, conclusi, tutti)
  const month = /^\d{4}-\d{2}$/.test(sp.get('month') ?? '') ? sp.get('month')! : undefined;
  const [text, setText] = useState(q);
  const [rows, setRows] = useState<AdminEventRow[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [scanned, setScanned] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // selezione per le azioni su più eventi
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  // aggiornamento funzionale: due modifiche ravvicinate non si sovrascrivono
  const set = (patch: Record<string, string | undefined>) =>
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
        return next;
      },
      { replace: true },
    );

  // ricerca con debounce
  useEffect(() => {
    const t = setTimeout(() => text !== q && set({ q: text.trim() || undefined }), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const query = (after?: string) => {
    const p = new URLSearchParams({ status, limit: '50' });
    if (month) {
      const { from, to } = monthRange(month);
      p.set('from', from);
      p.set('to', to);
    } else p.set('when', when);
    if (comune) p.set('comune', comune);
    if (category) p.set('category', category);
    if (q) p.set('q', q);
    if (after) p.set('cursor', after);
    return `/events?${p}`;
  };

  const load = async (after?: string) => {
    if (!canEdit) return;
    setLoading(true);
    setError(null);
    try {
      const page = await api<Page>(query(after));
      setRows((r) => (after ? [...r, ...page.items] : page.items));
      setCursor(page.cursor);
      setScanned((s) => (after ? s + page.scanned : page.scanned));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelected(new Set());
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, when, month, comune, category, q, canEdit]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  const bulk = async (action: BulkAction) => {
    const ids = [...selected].slice(0, 100);
    const what =
      action === 'category'
        ? `cambiare la categoria in «${categoryLabel(bulkCategory as Event['category'])}»`
        : BULK_LABEL[action].confirm;
    if (!window.confirm(`Vuoi ${what} per ${ids.length} eventi?`)) return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const r = await api<{ done: number; failed: Array<{ id: string; error: string }> }>(
        '/events/bulk',
        {
          method: 'POST',
          body: { ids, action, ...(action === 'category' ? { category: bulkCategory } : {}) },
        },
      );
      const title = (id: string) => rows.find((x) => x.id === id)?.title ?? id;
      setBulkResult(
        `${r.done} eventi aggiornati.` +
          (r.failed.length
            ? ` Non aggiornati: ${r.failed.map((f) => `«${title(f.id)}» (${f.error})`).join(', ')}.`
            : ''),
      );
      setSelected(new Set(r.failed.map((f) => f.id)));
      await load();
    } catch (e) {
      setBulkResult(`Errore: ${e instanceof ApiError ? e.message : String(e)}`);
    } finally {
      setBulkBusy(false);
    }
  };
  const pair = [...selected];

  const filtered = Boolean(comune || category || q);
  return (
    <main className="mx-auto max-w-[1400px] p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-h2">Eventi</h1>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Link to="/events/duplicates" className={btnSecondary}>
              <CopyCheck className="size-4" aria-hidden /> Doppioni
            </Link>
            <Link to="/events/new" className={btnPrimary}>
              <Plus className="size-4" aria-hidden /> Nuovo evento
            </Link>
          </div>
        )}
      </div>

      {/* filtri */}
      <div className="mt-4 flex flex-col gap-3 border border-surface-strong bg-surface p-3">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Stato">
          {STATUSES.map((s) => (
            <button
              key={s}
              role="tab"
              aria-selected={status === s}
              onClick={() => set({ status: s === 'published' ? undefined : s })}
              className={`h-9 px-3 text-sm ${status === s ? 'bg-fg font-medium text-page' : 'bg-page hover:bg-surface-strong'}`}
            >
              {STATUS_TABS[s]}
            </button>
          ))}
          <span className="mx-2 w-px self-stretch bg-surface-strong" aria-hidden />
          {WHEN.map((w) => (
            <button
              key={w.id}
              aria-pressed={!month && when === w.id}
              onClick={() =>
                set({ when: w.id === 'upcoming' ? undefined : w.id, month: undefined })
              }
              className={`h-9 px-3 text-sm ${!month && when === w.id ? 'bg-fg font-medium text-page' : 'bg-page hover:bg-surface-strong'}`}
            >
              {w.label}
            </button>
          ))}
          <span className="mx-2 w-px self-stretch bg-surface-strong" aria-hidden />
          <MonthPicker value={month} onChange={(m) => set({ month: m })} />
        </div>
        <div className="grid gap-2 md:grid-cols-[2fr_1fr_1fr_auto]">
          <label className="relative">
            <span className="sr-only">Cerca</span>
            <Search className="absolute top-3 left-2 size-4 text-muted" aria-hidden />
            <input
              type="search"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Cerca per titolo, comune, frazione o slug…"
              className={`${inputCls} pl-8`}
            />
          </label>
          <ComunePicker
            value={comune}
            onChange={(v) => set({ comune: v })}
            allowEmpty
            placeholder="Tutti i comuni"
          />
          <select
            value={category}
            onChange={(e) => set({ category: e.target.value || undefined })}
            className={inputCls}
            aria-label="Categoria"
          >
            <option value="">Tutte le categorie</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {categoryLabel(c)}
              </option>
            ))}
          </select>
          {filtered && (
            <button
              onClick={() => {
                setText('');
                set({ comune: undefined, category: undefined, q: undefined });
              }}
              className={btnGhost}
            >
              <X className="size-4" aria-hidden /> Azzera
            </button>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}

      {/* azioni sugli eventi selezionati */}
      {canEdit && selected.size > 0 && (
        <div className="sticky top-[6.75rem] z-10 mt-4 md:top-14 flex flex-wrap items-center gap-2 border border-fg bg-page p-2 text-sm">
          <span className="px-1 font-medium">{selected.size} selezionati</span>
          {(['published', 'draft', 'cancelled', 'archived'] as const)
            .filter((a) => a !== status)
            .map((a) => (
              <button
                key={a}
                onClick={() => void bulk(a)}
                disabled={bulkBusy}
                className={btnSecondary}
              >
                {BULK_LABEL[a].button}
              </button>
            ))}
          <span className="mx-1 w-px self-stretch bg-surface-strong" aria-hidden />
          <select
            value={bulkCategory}
            onChange={(e) => setBulkCategory(e.target.value)}
            className={`${inputBaseCls} w-auto`}
            aria-label="Nuova categoria"
          >
            <option value="">Cambia categoria…</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {categoryLabel(c)}
              </option>
            ))}
          </select>
          <button
            onClick={() => void bulk('category')}
            disabled={bulkBusy || !bulkCategory}
            className={btnSecondary}
          >
            Applica
          </button>
          {selected.size === 2 && (
            <>
              <span className="mx-1 w-px self-stretch bg-surface-strong" aria-hidden />
              <Link
                to={`/events/merge?target=${pair[0]}&source=${pair[1]}`}
                className={btnSecondary}
              >
                <Merge className="size-4" aria-hidden /> Unisci i due eventi
              </Link>
            </>
          )}
          <button onClick={() => setSelected(new Set())} className={`${btnGhost} ml-auto`}>
            <X className="size-4" aria-hidden /> Deseleziona
          </button>
        </div>
      )}
      {bulkResult && (
        <p role="status" className="mt-2 bg-surface p-2 text-sm">
          {bulkResult}
        </p>
      )}

      {/* elenco */}
      <div className="relative mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-sm md:min-w-[860px]">
          <thead>
            <tr className="border-b-2 border-fg text-left text-label uppercase tracking-[0.04em] text-muted">
              {canEdit && (
                <th className="w-8 py-2 pr-2 font-medium">
                  <input
                    type="checkbox"
                    aria-label="Seleziona tutti"
                    checked={allSelected}
                    onChange={() =>
                      setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))
                    }
                  />
                </th>
              )}
              <th className="py-2 pr-3 font-medium" title="Ordinati per data di fine">
                Date ↓ fine
              </th>
              <th className="py-2 pr-3 font-medium">Evento</th>
              <th className="hidden py-2 pr-3 font-medium md:table-cell">Categoria</th>
              <th className="hidden py-2 pr-3 font-medium md:table-cell">Stato</th>
              <th className="hidden py-2 pr-3 font-medium md:table-cell">Aggiornato</th>
              <th className="py-2 font-medium">
                <span className="sr-only">Azioni</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => navigate(`/events/${r.id}`)}
                className={`cursor-pointer border-b border-surface-strong align-top hover:bg-surface ${selected.has(r.id) ? 'bg-surface' : ''}`}
              >
                {canEdit && (
                  <td className="py-2 pr-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Seleziona ${r.title}`}
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                  </td>
                )}
                <td className="py-2 pr-3 text-xs md:whitespace-nowrap md:text-sm">
                  {formatRange(r.startDate, r.endDate)}
                </td>
                <td className="py-2 pr-3">
                  <Link
                    to={`/events/${r.id}`}
                    className="font-medium hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {r.title}
                  </Link>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted">
                    <span>
                      {[r.frazione, comuneLabel(r.comuneId) || r.comune].filter(Boolean).join(', ')}
                    </span>
                    {r.featured && (
                      <span className="inline-flex items-center gap-0.5" title="In evidenza">
                        <Star className="size-3.5 fill-current" aria-hidden /> in evidenza
                      </span>
                    )}
                    {r.locked > 0 && (
                      <span
                        className="inline-flex items-center gap-0.5"
                        title="Campi bloccati: la pipeline non li sovrascrive"
                      >
                        <Lock className="size-3.5" aria-hidden /> {r.locked}
                      </span>
                    )}
                    {r.hasImage && <ImageIcon className="size-3.5" aria-label="Con immagine" />}
                  </div>
                  {/* telefono: tipo e stato sotto il titolo, al posto delle colonne */}
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs md:hidden">
                    <StatusBadge status={r.status} />
                    <span className="text-muted">{categoryLabel(r.category)}</span>
                  </div>
                </td>
                <td className="hidden py-2 pr-3 md:table-cell">{categoryLabel(r.category)}</td>
                <td className="hidden py-2 pr-3 md:table-cell">
                  <StatusBadge status={r.status} />
                </td>
                <td className="hidden py-2 pr-3 whitespace-nowrap text-muted md:table-cell">
                  {formatStamp(r.updatedAt)}
                </td>
                <td className="py-2 text-right">
                  {r.status !== 'draft' && (
                    <a
                      href={`${config.siteUrl}/events/${r.slug}`}
                      target="_blank"
                      rel="noopener"
                      onClick={(e) => e.stopPropagation()}
                      className={btnGhost}
                      title="Apri sul sito"
                    >
                      <ExternalLink className="size-4" aria-hidden />
                      <span className="sr-only">Apri sul sito</span>
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-muted">
        {loading ? (
          <span>Caricamento…</span>
        ) : (
          <span>
            {rows.length === 0 ? 'Nessun evento con questi filtri.' : `${rows.length} eventi`}
            {month && ` · ${monthLabel(month)}`}
            {q && ` · ${scanned} esaminati`}
          </span>
        )}
        {cursor && !loading && (
          <button onClick={() => load(cursor)} className={btnSecondary}>
            {q ? 'Continua a cercare' : 'Carica altri'}
          </button>
        )}
      </div>
    </main>
  );
}
